import React, { useRef, useState, useMemo } from 'react';
import axios from 'axios';
import { X, Upload } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogForm = ({ formData, setFormData, onSubmit, onCancel, isEditing, error }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const quillModules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['blockquote', 'code-block'],
      ['link'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['clean']
    ]
  }), []);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API_URL}/api/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, featured_image: data.url }));
    } catch (err) {
      console.error('Image upload failed:', err);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const getImageSrc = (url) => {
    if (!url) return '';
    if (url.startsWith('/api')) return `${API_URL}${url}`;
    return url;
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="blog-modal">
      <div className="bg-white rounded-sm max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-2xl font-bold text-primary" data-testid="modal-title">
            {isEditing ? 'Edit Blog Post' : 'Add New Blog Post'}
          </h2>
          <button onClick={onCancel} className="text-text_muted hover:text-primary" data-testid="modal-close">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded text-red-700" data-testid="modal-error">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-primary mb-2">Title *</label>
              <input type="text" name="title" value={formData.title} onChange={handleChange} required className="w-full px-4 py-3 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" data-testid="input-title" />
            </div>

            <div>
              <label className="block text-sm font-medium text-primary mb-2">Featured Image</label>
              <div className="space-y-3">
                {formData.featured_image && (
                  <div className="relative w-full h-40 rounded overflow-hidden border border-border">
                    <img src={getImageSrc(formData.featured_image)} alt="Preview" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setFormData(prev => ({ ...prev, featured_image: '' }))} className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full" data-testid="remove-blog-image">
                      <X size={14} />
                    </button>
                  </div>
                )}
                <div>
                  <label className="block text-xs text-text_muted mb-1 font-medium">Choose from your device:</label>
                  <input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading} className="w-full text-sm text-text_muted file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-opacity-90 file:cursor-pointer cursor-pointer border border-input rounded" data-testid="blog-file-input" />
                  {uploading && <p className="text-xs text-accent mt-1 font-medium">Uploading...</p>}
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-border"></div>
                  <span className="text-xs text-text_muted">OR</span>
                  <div className="flex-1 h-px bg-border"></div>
                </div>
                <input type="text" name="featured_image" value={formData.featured_image} onChange={handleChange} placeholder="Paste image URL here..." className="w-full px-4 py-2 border border-input rounded focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm" data-testid="input-featured-image" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-primary mb-2">Content *</label>
              <div className="border border-input rounded overflow-hidden" data-testid="rich-text-editor">
                <ReactQuill
                  theme="snow"
                  value={formData.content}
                  onChange={(val) => setFormData(prev => ({ ...prev, content: val }))}
                  modules={quillModules}
                  placeholder="Write your blog post content here..."
                  style={{ minHeight: '250px' }}
                />
              </div>
            </div>

            <div className="flex space-x-4 pt-2">
              <button type="submit" className="flex-1 px-6 py-3 bg-accent text-white rounded font-semibold hover:bg-opacity-90 transition-all" data-testid="modal-submit">
                {isEditing ? 'Update Blog' : 'Create Blog'}
              </button>
              <button type="button" onClick={onCancel} className="px-6 py-3 bg-surface text-text_main rounded font-semibold hover:bg-border transition-all" data-testid="modal-cancel">
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlogForm;
